import { Button, buttonVariants } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getTorrentMetadataFromEvent } from '@/lib/event-metadata'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Cancel01Icon,
  Copy01Icon,
  DownloadIcon,
  FilmIcon,
  ImageIcon,
  Loading03Icon,
  MusicNote01Icon
} from '@hugeicons/core-free-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const WEBTORRENT_SCRIPT_URL =
  'https://cdn.jsdelivr.net/npm/webtorrent@3.0.21/dist/webtorrent.min.js'
// the service worker script must be same-origin, so it's vendored in public/
const WEBTORRENT_SW_URL = '/webtorrent/sw.min.js'
const WEBTORRENT_SW_SCOPE = '/webtorrent/'
// the sw only intercepts urls under scope + 'webtorrent/', so the server path
// gets an extra prefix to match that
const WEBTORRENT_SERVER_PATH = '/webtorrent/webtorrent'
const FALLBACK_WSS_TRACKERS = ['wss://tracker.openwebtorrent.com', 'wss://tracker.webtorrent.dev']
const VIDEO_EXTS = ['mp4', 'm4v', 'webm', 'mkv', 'mov', 'avi', 'ogv', '3gp']
const AUDIO_EXTS = ['mp3', 'm4a', 'aac', 'flac', 'ogg', 'oga', 'opus', 'wav']
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp']

type TMediaKind = 'video' | 'audio' | 'image'

let webtorrentLoadPromise: Promise<any> | null = null

function loadWebTorrent(): Promise<any> {
  const existing = (window as any).WebTorrent
  if (existing) return Promise.resolve(existing)
  if (!webtorrentLoadPromise) {
    webtorrentLoadPromise = new Promise((resolve, reject) => {
      // the bundle is an ES module with no global side effects, so import it
      // from an inline module script and assign the global ourselves
      const script = document.createElement('script')
      script.type = 'module'
      script.textContent = `import WT from '${WEBTORRENT_SCRIPT_URL}'
window.WebTorrent = WT
window.dispatchEvent(new Event('webtorrent-loaded'))`
      script.onerror = () => {
        webtorrentLoadPromise = null
        reject(new Error('Failed to load the WebTorrent library'))
      }
      window.addEventListener('webtorrent-loaded', () => resolve((window as any).WebTorrent), {
        once: true
      })
      document.body.appendChild(script)
    })
  }
  return webtorrentLoadPromise
}

let swRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null

function registerWebTorrentSw(): Promise<ServiceWorkerRegistration> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = (async () => {
      const registration = await navigator.serviceWorker.register(WEBTORRENT_SW_URL, {
        scope: WEBTORRENT_SW_SCOPE
      })
      await navigator.serviceWorker.ready
      return registration
    })().catch((err) => {
      swRegistrationPromise = null
      throw new Error('Failed to register the streaming service worker: ' + err.message)
    })
  }
  return swRegistrationPromise
}

function extOf(name: string) {
  return name.split('.').pop()?.toLowerCase() ?? ''
}

function playableKind(name: string): TMediaKind | undefined {
  const ext = extOf(name)
  if (VIDEO_EXTS.includes(ext)) return 'video'
  if (AUDIO_EXTS.includes(ext)) return 'audio'
  if (IMAGE_EXTS.includes(ext)) return 'image'
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0 || isNaN(bytes)) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n >= 100 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`
}

const MAX_SHOWN_FILES = 20
const MAX_SHOWN_TRACKERS = 10

export default function Torrent({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const metadata = useMemo(() => getTorrentMetadataFromEvent(event), [event])

  const magnet = useMemo(() => {
    if (!metadata.infoHash) return undefined
    let uri = `magnet:?xt=urn:btih:${metadata.infoHash}`
    for (const hash of metadata.infoHash) uri += `&xt=urn:btih:${hash}`
    uri += `&dn=${encodeURIComponent(metadata.title)}`
    for (const tracker of metadata.trackers) uri += `&tr=${encodeURIComponent(tracker)}`
    return uri
  }, [metadata])

  const clientRef = useRef<any>(null)
  const torrentRef = useRef<any>(null)
  const cancelRef = useRef<() => void>(() => {})
  const statsIntervalRef = useRef<ReturnType<typeof setInterval>>()
  const mediaElRef = useRef<HTMLVideoElement | HTMLAudioElement | HTMLImageElement | null>(null)
  const [activeMedia, setActiveMedia] = useState<{ index: number; kind: TMediaKind }>()
  const [pendingIndex, setPendingIndex] = useState<number>()
  const [connecting, setConnecting] = useState(false)
  const [stats, setStats] = useState<{
    peers: number
    speed: number
    progress: number
    downloaded?: number
    total?: number
  }>()
  const [error, setError] = useState<string>()

  useEffect(
    () => () => {
      cancelRef.current()
      clientRef.current?.destroy()
    },
    []
  )

  async function play(fileIndex: number) {
    if (!magnet || connecting || activeMedia) return

    let cancelled = false
    cancelRef.current = () => {
      cancelled = true
      clearInterval(statsIntervalRef.current)
      Promise.resolve(torrentRef.current?.destroy()).catch(() => undefined)
      torrentRef.current = null
      setConnecting(false)
      setActiveMedia(undefined)
      setPendingIndex(undefined)
      setStats(undefined)
    }

    setPendingIndex(fileIndex)
    setError(undefined)
    setStats(undefined)

    try {
      setConnecting(true)
      const WebTorrent = await loadWebTorrent()
      if (!WebTorrent.WEBRTC_SUPPORT) throw new Error(t('WebRTC is not supported here'))
      ;(window as any).WEBTORRENT_ANNOUNCE ||= FALLBACK_WSS_TRACKERS
      if (!clientRef.current) {
        // browsers can't use udp trackers or dht, only websocket ones, and the
        // sw-based server is what allows streaming into media elements
        const controller = await registerWebTorrentSw()
        clientRef.current = new WebTorrent()
        clientRef.current.createServer({ controller, pathname: WEBTORRENT_SERVER_PATH })
      }
      if (cancelled) return

      const torrent = await new Promise<any>((resolve, reject) => {
        const added = clientRef.current.add(magnet, (tor: any) => resolve(tor))
        added.on('error', reject)
        torrentRef.current = added
      })
      if (cancelled) return

      setConnecting(false)
      const file = torrent.files[fileIndex]
      setPendingIndex(undefined)
      setActiveMedia({ index: fileIndex, kind: playableKind(file.name)! })

      clearInterval(statsIntervalRef.current)
      statsIntervalRef.current = setInterval(() => {
        const tor = torrentRef.current
        const file = tor?.files?.[fileIndex]
        if (!tor || !file) return
        setStats({
          peers: tor.numPeers,
          speed: tor.downloadSpeed,
          progress: file.progress,
          downloaded: file.downloaded,
          total: file.length
        })
        if (tor.done) clearInterval(statsIntervalRef.current)
      }, 500)
    } catch (err) {
      if (!cancelled) {
        setConnecting(false)
        setError((err as Error).message || String(err))
      }
    }
  }

  function stop() {
    cancelRef.current()
  }

  // streamTo needs the rendered element, so it runs as an effect after the
  // media element mounts
  useEffect(() => {
    const torrent = torrentRef.current
    const index = activeMedia?.index
    if (!torrent?.ready || index === undefined) return
    const el = mediaElRef.current
    if (!el) return
    try {
      torrent.files[index].streamTo(el)
    } catch (err) {
      setActiveMedia(undefined)
      setError(String((err as Error).message || err))
    }
  }, [activeMedia])

  const shownFiles = metadata.files.slice(0, MAX_SHOWN_FILES)

  return (
    <div className={cn('space-y-2', className)}>
      <div className="text-lg font-semibold break-words">{metadata.title}</div>
      {(metadata.tags.length > 0 || event.content) && (
        <div className="flex items-center gap-2">
          {metadata.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      {event.content && (
        <div className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6 break-words">
          {event.content}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {magnet && (
          <>
            <a
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              href={magnet}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <HugeiconsIcon icon={DownloadIcon} />
              {t('Open in torrent app')}
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard.writeText(magnet)
                toast.success(t('Magnet link copied to clipboard'))
              }}
            >
              <HugeiconsIcon icon={Copy01Icon} />
              {t('Copy magnet')}
            </Button>
          </>
        )}
      </div>
      {(connecting || stats) && (
        <div className="flex items-center gap-2 text-sm">
          {connecting ? (
            <span className="text-muted-foreground">{t('Connecting to peers…')}</span>
          ) : (
            <>
              <span className="text-muted-foreground whitespace-nowrap">
                {Math.min(100, Math.round((stats?.progress ?? 0) * 100))}%
              </span>
              <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden min-w-18">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${(stats?.progress ?? 0) * 100}%` }}
                />
              </div>
              <span className="text-muted-foreground whitespace-nowrap">
                {stats?.downloaded ? formatBytes(stats.downloaded) : '0'}/
                {stats?.total ? formatBytes(stats.total) : '???'} · {stats?.peers ?? 0} peer(s) ·{' '}
                {formatBytes(stats?.speed ?? 0)}/s
              </span>
            </>
          )}
          <Button
            variant="ghost-destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              stop()
            }}
          >
            <HugeiconsIcon icon={Cancel01Icon} />
            {t('Cancel')}
          </Button>
        </div>
      )}
      {error && <div className="text-sm text-destructive">{error}</div>}
      {activeMedia?.kind === 'video' && (
        <video
          ref={(el) => {
            mediaElRef.current = el
          }}
          controls
          autoPlay
          className="w-full max-h-[80vh] bg-black rounded-lg"
        />
      )}
      {activeMedia?.kind === 'audio' && (
        <audio
          ref={(el) => {
            mediaElRef.current = el
          }}
          controls
          autoPlay
          className="w-full"
        />
      )}
      {activeMedia?.kind === 'image' && (
        <img
          ref={(el) => {
            mediaElRef.current = el
          }}
          className="max-h-[80vh] rounded-lg mx-auto block"
        />
      )}
      {metadata.files.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-medium">
            {t('{{count}} files · {{size}}', {
              count: metadata.files.length,
              size: formatBytes(metadata.totalSize),
              defaultValue: `${metadata.files.length} files`
            })}
          </div>
          {shownFiles.map((file, i) => {
            const kind = playableKind(file.name)
            const KindIcon =
              kind === 'video'
                ? FilmIcon
                : kind === 'audio'
                  ? MusicNote01Icon
                  : kind === 'image'
                    ? ImageIcon
                    : undefined
            return (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground truncate">
                  {file.name}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {file.size !== undefined && (
                    <span className="text-muted-foreground">{formatBytes(file.size)}</span>
                  )}
                  {kind && (
                    <button
                      className="cursor-pointer hover:text-primary disabled:opacity-50"
                      title={t('Play')}
                      disabled={connecting || !!activeMedia}
                      onClick={(e) => {
                        e.stopPropagation()
                        play(i)
                      }}
                    >
                      {pendingIndex === i ? (
                        <HugeiconsIcon icon={Loading03Icon} size={16} className="animate-spin" />
                      ) : (
                        <HugeiconsIcon icon={KindIcon!} size={16} />
                      )}
                    </button>
                  )}
                </span>
              </div>
            )
          })}
          {metadata.files.length > MAX_SHOWN_FILES && (
            <div className="text-xs text-muted-foreground">
              {t('+ {{count}} more files', { count: metadata.files.length - MAX_SHOWN_FILES })}
            </div>
          )}
        </div>
      )}
      {metadata.trackers.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-medium">{t('Trackers')}</div>
          {metadata.trackers.slice(0, MAX_SHOWN_TRACKERS).map((tracker) => (
            <div key={tracker} className="font-mono text-xs text-muted-foreground truncate">
              {tracker}
            </div>
          ))}
          {metadata.trackers.length > MAX_SHOWN_TRACKERS && (
            <div className="text-xs text-muted-foreground">
              {t('+ {{count}} more', { count: metadata.trackers.length - MAX_SHOWN_TRACKERS })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
